/*
  Responsible for loading and formatting the account/publication data
  to be used by the AccountSwitcher component.
*/

import { withCookieJar } from '../../utils/cookie_jar';

export class Account {
  constructor(switcherLoader, data) {
    this.switcherLoader = switcherLoader;
    this.name = data.name;
    this.id = parseInt(data.id, 0);
  }

  get publications() {
    return this.switcherLoader.publications.filter(
      publication => publication.accountId === this.id
    );
  }

  serialize() {
    return {
      name: this.name,
      id: this.id,
      children: this.publications.map(publication => publication.serialize())
    };
  }
}

export class Publication {
  constructor(switcherLoader, data) {
    this.switcherLoader = switcherLoader;
    this.name = data.name;
    this.id = parseInt(data.id, 0);
    this.accountId = parseInt(data.accountId, 0);
  }

  get account() {
    return this.switcherLoader.accounts.find(account => account.id === this.accountId);
  }

  serialize() {
    return {
      name: this.name,
      id: this.id,
      accountId: this.accountId
    };
  }
}

// This class must be re-initialized whenever permission data or the current page changes
// The publications async permission checks are cached so serailization is synchronous and fast
class AccountSwitcherLoader {
  // Matches campaigns and media lib
  static ACCOUNT_ID_MATCHER = /(\/organizations\/)(\d*)|(account_id=)(\d*)/;

  // Matches pub settings, media lib, and analytics
  static PUBLICATION_ID_MATCHER = /(\/publications\/)(\d*)|(brand_profile_id=)(\d*)|(publication_id=)(\d*)|(\/analytics\/)(\d*)/;

  // Matches id of nested resource, and also the edit path
  // i.e. organizations/:org_id/campaigns/:id or publications/:pub_id/teams/:id/edit
  static RESOURCE_ID_MATCHER = /\/\d+$|\/\d+\/edit$/;

  static accountIdFromLocation(location = null) {
    const { pathname, search } = location || window.location;
    const match = (pathname + search).match(this.ACCOUNT_ID_MATCHER) || [];
    // For each possible match group
    return match[2] || match[4];
  }

  static publicationIdFromLocation(location = null) {
    const { pathname, search } = location || window.location;
    const match = (pathname + search).match(this.PUBLICATION_ID_MATCHER) || [];
    // For each possible match group
    return match[2] || match[4] || match[6] || match[8];
  }

  static handleAccountScopedPage(accountId) {
    const changeLocation = this.accountIdFromLocation();
    if (changeLocation) {
      // Assuming that capture groups 1 or 3 have been matched before the id
      let newLocation = window.location.href.replace(this.ACCOUNT_ID_MATCHER, `$1$3${accountId}`);
      // HACK: remove `/:id` when switching accounts on show pages
      newLocation = newLocation.replace(this.RESOURCE_ID_MATCHER, '');

      window.location = newLocation;
    }
    return changeLocation;
  }

  static handlePublicationScopedPage(publicationId) {
    const changeLocation = this.publicationIdFromLocation();
    if (changeLocation) {
      // Assuming that capture groups 1, 3, 5, 7 have been matched before the id
      let newLocation = window.location.href.replace(
        this.PUBLICATION_ID_MATCHER,
        `$1$3$5$7${publicationId}`
      );
      // HACK: remove s_ids search param from analytics urls when switching pubs
      newLocation = this.removeURLParameter(newLocation, 's_id');
      // HACK: remove `/:id` or `/:id/edit` when switching pubs on edit/show pages
      newLocation = newLocation.replace(this.RESOURCE_ID_MATCHER, '');

      window.location = newLocation;
    }

    return changeLocation;
  }

  static removeURLParameter(url, paramTarget) {
    const urlParts = url.split('?');
    if (urlParts.length === 1) return url;

    const prefix = `${window.encodeURIComponent(paramTarget)}=`;
    const params = urlParts[1].split(/[&;]/g);

    return `${urlParts[0]}?${params.filter(p => !p.startsWith(prefix))}`;
  }

  constructor(userData) {
    this.publications = userData.brandProfiles.map(bp => new Publication(this, bp));
    this.accounts = userData.accounts.map(account => new Account(this, account));
    this.loadActiveData();
  }

  get activeAccount() {
    return this.accounts.find(account => account.id === parseInt(this.activeAccountId, 0));
  }

  set activeAccount({ id }) {
    this.activeAccountId = id;
  }

  get activePublication() {
    return this.publications.find(
      publication => publication.id === parseInt(this.activePublicationId, 0)
    );
  }

  set activePublication({ id }) {
    this.activePublicationId = id;
  }

  loadActiveData() {
    let activeAccount = this.activeAccount || this.accounts[0];
    let activePublication = this.activePublication || activeAccount.publications[0];

    const currentAccountId = parseInt(AccountSwitcherLoader.accountIdFromLocation(), 0);

    if (currentAccountId) {
      activeAccount = this.accounts.find(account => account.id === currentAccountId);
      activePublication =
        activeAccount.id === activePublication.accountId
          ? activePublication
          : activeAccount.publications[0];
    }

    const currentPubId = parseInt(AccountSwitcherLoader.publicationIdFromLocation(), 0);

    if (currentPubId) {
      activePublication = this.publications.find(publication => publication.id === currentPubId);
      activeAccount = activePublication.account;
    }

    this.activeAccount = activeAccount;
    this.activePublication = activePublication;
  }

  serialize() {
    return this.accounts.map(account => account.serialize());
  }
}

export default withCookieJar(AccountSwitcherLoader, 'activeAccountId', 'activePublicationId');



// WEBPACK FOOTER //
// ./app/javascript/app/components/nav/account_switcher_loader.js